import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as flagsApi from "@/api/featureFlags.api";
import type { FeatureFlag } from "@/types";

interface FeatureFlagsState {
  items: FeatureFlag[];
  current: FeatureFlag | null;
  loading: boolean;
  error: string | null;
}

const initialState: FeatureFlagsState = {
  items: [],
  current: null,
  loading: false,
  error: null,
};

export const fetchFeatureFlags = createAsyncThunk(
  "featureFlags/fetchAll",
  async (projectId?: string) => flagsApi.getFeatureFlags(projectId),
);

export const fetchFeatureFlag = createAsyncThunk(
  "featureFlags/fetchOne",
  async (id: string) => flagsApi.getFeatureFlag(id),
);

export const createFeatureFlag = createAsyncThunk(
  "featureFlags/create",
  async (data: Partial<FeatureFlag> & { projectId: string }) =>
    flagsApi.createFeatureFlag(data),
);

export const updateFeatureFlag = createAsyncThunk(
  "featureFlags/update",
  async ({ id, data }: { id: string; data: Partial<FeatureFlag> }) =>
    flagsApi.updateFeatureFlag(id, data),
);

export const toggleFeatureFlag = createAsyncThunk(
  "featureFlags/toggle",
  async (id: string) => flagsApi.toggleFeatureFlag(id),
);

export const deleteFeatureFlag = createAsyncThunk(
  "featureFlags/delete",
  async (id: string) => {
    await flagsApi.deleteFeatureFlag(id);
    return id;
  },
);

const featureFlagsSlice = createSlice({
  name: "featureFlags",
  initialState,
  reducers: {
    setCurrent(state, action) {
      state.current = action.payload;
    },
    clearCurrent(state) {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch feature flags";
      })
      .addCase(fetchFeatureFlag.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(createFeatureFlag.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(updateFeatureFlag.fulfilled, (state, action) => {
        const idx = state.items.findIndex((f) => f.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
        if (state.current?.id === action.payload.id)
          state.current = action.payload;
      })
      .addCase(toggleFeatureFlag.fulfilled, (state, action) => {
        const idx = state.items.findIndex((f) => f.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
        if (state.current?.id === action.payload.id)
          state.current = action.payload;
      })
      .addCase(deleteFeatureFlag.fulfilled, (state, action) => {
        state.items = state.items.filter((f) => f.id !== action.payload);
        if (state.current?.id === action.payload) state.current = null;
      });
  },
});

export const { setCurrent, clearCurrent } = featureFlagsSlice.actions;
export default featureFlagsSlice.reducer;
